/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackItemProps, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Container } from 'src/core/components/Container';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getStylist } from 'src/core/themes/luisStylist';
import { ConversationPublishSlotStore } from 'src/luis/core/stores/ConversationPublishSlotStore';
import { EndpointHitsCard } from 'src/luis/projects/analytics/components/PublishCard/EndpointHitsCard/EndpointHitsCard';
import {
    PublishStatusCard,
    PublishStatusCardProps
} from 'src/luis/projects/analytics/components/PublishCard/PublishStatusCard/PublishStatusCard';

const { styleComponent } = getStylist('Dashboard');

const messages = defineMessages({
    title: {
        id: 'analytics.publishCard.title',
        defaultMessage: 'Published app'
    },
    titleDescription: {
        id: 'analytics.publishCard.titleDescription',
        defaultMessage: 'Select the published slot to review the current state of the app including services, regions and endpoint hits.'
    },
    allRegions: {
        id: 'analytics.publishCard.allRegions',
        defaultMessage: 'All regions'
    }
});

const EndpointChart = styleComponent<IStackItemProps>(StackItem)('EndpointChart', theme => ({
    height: 'auto',
    overflow: 'hidden',
    boxShadow: theme.cardBoxShadowing
}));

const PublishStatus = styleComponent<IStackItemProps>(StackItem)('PublishStatus', { height: 'auto' });

/**
 * The section that represents the publish status of the application.
 */
export const PublishCard = observer(() => {
    const { localize } = useLocalization();
    const { isXXLargeOrGreater } = useResponsiveMode();
    const { dispatcher, rootStore } = useGlobalState();

    React.useEffect(() => {
        dispatcher.dispatch('loadAppSettings');
        dispatcher.dispatch('loadAppAzureCsAccounts');
    }, [dispatcher.dispatch]);

    const appStore = rootStore.currentLuisAppStore;
    const { analyticsStore } = appStore.currentAppVersionStore;

    // Calculate the publish status of the current application.
    const isAppPublishedOnStaging = Boolean(appStore.publishSlots.staging);
    const isAppPublishedOnProduction = Boolean(appStore.publishSlots.production);
    const isAppPublished = isAppPublishedOnProduction || isAppPublishedOnStaging;

    // Prepare the production and staging publish slots.
    const productionPublishSlot: Partial<ConversationPublishSlotStore> = appStore.publishSlots.production || null;
    const stagingPublishSlot: Partial<ConversationPublishSlotStore> = appStore.publishSlots.staging || null;

    // Calculate the latest publish slot.
    const isProductionLatestPublishSlot =
        !stagingPublishSlot || productionPublishSlot?.lastPublishedDate > stagingPublishSlot?.lastPublishedDate;
    const latestPublishedAppSlot = isProductionLatestPublishSlot ? productionPublishSlot : stagingPublishSlot;

    // Prepare the latest publish slot object to preview.
    let publishSlotName = null;

    if (isAppPublished) {
        publishSlotName = isProductionLatestPublishSlot ? 'Production' : 'Staging';
    }

    const latestPublishSlot: PublishStatusCardProps = {
        publishSlot: publishSlotName,
        versionId: latestPublishedAppSlot?.versionId,
        publishDate: latestPublishedAppSlot?.lastPublishedDate,
        externalServices: {
            isSpeechPrimingOn: latestPublishedAppSlot?.isSpeechPrimingOn,
            isSentimentAnalysisOn: latestPublishedAppSlot?.isSentimentAnalysisOn
        },
        regions: appStore.appSettings?.isPublic
            ? [localize(messages.allRegions)]
            : appStore.azureAccounts.map(item => item.region).filter(isDuplicate)
    };

    React.useEffect(() => dispatcher.dispatch('getPublishSettings', { isStaging: latestPublishSlot.publishSlot === 'Staging' }), [
        dispatcher.dispatch,
        latestPublishSlot.publishSlot
    ]);

    const handleHistoryDurationChange = (duration: number) => {
        const startDate = new Date();
        startDate.setDate(new Date().getDate() - duration);
        dispatcher.dispatch('loadEndpointHitsHistory', { startDate, endDate: new Date() });
    };

    return (
        <Container size="main" overflow={false}>
            <EditableTitle title={localize(messages.title)} description={localize(messages.titleDescription)} />

            <Stack horizontal={isXXLargeOrGreater} verticalFill tokens={{ childrenGap: 10 }}>
                <PublishStatus verticalFill>
                    <PublishStatusCard {...latestPublishSlot} />
                </PublishStatus>

                <EndpointChart verticalFill grow={1}>
                    <EndpointHitsCard
                        endpointHits={analyticsStore.appEndpointHits}
                        onHistoryDurationChanged={handleHistoryDurationChange}
                    />
                </EndpointChart>
            </Stack>
        </Container>
    );
});

/**
 * Checks the duplicates. Returns true if the item is already
 * exists in the array false else.
 *
 * @param item Item to be checked if it is duplicate.
 * @param index The item index in the array.
 * @param arr The array to check the duplicates on.
 */
const isDuplicate = (item: string, index: number, arr: string[]): boolean => arr.indexOf(item) === index;
